// =============================================================================
// FONDAMENTS PÉDAGOGIQUES — Formation du conducteur
// =============================================================================
// Trame officielle conforme au cours "La pédagogie en formation du conducteur"
// (Claude Benninger, TP-ECSR CCR1, 2022)
//
// Plan du document source :
//   I.   La relation pédagogique
//   II.  La formation du conducteur (matrice GDE + REMC)
//   III. La pédagogie par objectifs (PPO)
//   IV.  Formuler un objectif (spécifique / opérationnel)
//   V.   Les méthodes pédagogiques (expositives + actives)
//   VI.  Les évaluations (en formation, hors formation, questionnement)
//   VII. Définitions complémentaires (attitudes, représentations, motivations…)
// =============================================================================

export const META_FONDAMENTS = {
  titre: "La pédagogie en formation du conducteur",
  source: 'Claude Benninger — TP ECSR CCR1 (2022)',
  contexte: "Cadre pédagogique français officiel du métier d'ECSR. Mobilisable par tout candidat au CCS pour démontrer sa maîtrise des fondements pédagogiques.",
};

// =============================================================================
// I. CADRE OFFICIEL FRANÇAIS
// =============================================================================
export const CADRE_OFFICIEL = {
  intitule: "Cadre officiel français de la formation à la conduite",
  description: "Depuis 2014, le REMC (Référentiel pour une Éducation à une Mobilité Citoyenne) remplace le PNF (Programme National de Formation, 2002-2014). Le REMC s'appuie sur la matrice GDE (Goals for Driver Education), cadre européen.",
  evolution: [
    { date: 'Avant 2002', label: 'Programme National de Formation (PNF v1)' },
    { date: '2002-2014', label: 'PNF v2 + Pédagogie Par Objectifs' },
    { date: 'Depuis 2014', label: 'REMC (4 niveaux GDE) — référentiel actuel' },
  ],
  livretApprentissage: {
    intitule: "Livret d'apprentissage (catégorie A — moto)",
    competences: [
      "C1 — Maîtriser le maniement du véhicule à allure réduite et à allure plus élevée",
      "C2 — Appréhender la route et circuler dans des conditions normales",
      "C3 — Circuler dans des conditions difficiles et partager la route avec les autres usagers",
      "C4 — Pratiquer une conduite autonome, sûre et économique",
    ],
    note: "Ces 4 compétences ne sont PAS celles du REMC (à ne pas confondre avec le jury).",
  },
};

// =============================================================================
// II. MATRICE GDE — Goals for Driver Education
// =============================================================================
export const MATRICE_GDE = {
  intitule: "La matrice GDE (Goals for Driver Education)",
  origine: "Cadre européen (Hatakka & al., 2002) adopté par la France via le REMC. 4 niveaux d'acquisition hiérarchisés.",
  niveaux: [
    {
      numero: 1,
      intitule: "Maîtrise du véhicule",
      description: "Compétences techniques de base : démarrage, équilibre, freinage, changement de vitesse, conduite en circulation simple.",
      enjeux: ["Maniement", "Maîtrise mécanique", "Sécurité immédiate"],
      methodes: ['Démonstrative', 'Active sur plateau', 'Conduite commentée'],
    },
    {
      numero: 2,
      intitule: "Maîtrise de situations de circulation",
      description: "Adapter sa conduite à des situations variées : agglomération, route, autoroute, météo, trafic dense, partage de la voie.",
      enjeux: ["Adaptation", "Anticipation", "Communication avec les autres usagers"],
      methodes: ['Active en circulation', 'Étude de cas', 'Conduite commentée'],
    },
    {
      numero: 3,
      intitule: "Objectifs liés à la conduite et au contexte",
      description: "Comprendre le pourquoi du déplacement : choix du véhicule, choix du moment, choix de l'itinéraire, motivations. Le conducteur prend conscience de ses choix.",
      enjeux: ["Choix raisonnés", "Motivations conscientes", "Risque accepté"],
      methodes: ['Étude de cas', 'Jeu de rôle', 'Débat dirigé', 'Auto-évaluation'],
    },
    {
      numero: 4,
      intitule: "Aspirations personnelles et compétences pour la vie",
      description: "Niveau le plus élevé : conscience de soi, gestion des émotions, du stress, de la pression sociale, des biais cognitifs (Dunning-Kruger, locus de contrôle).",
      enjeux: ["Conscience de soi", "Gestion émotionnelle", "Citoyenneté"],
      methodes: ['Atelier réflexif', 'Auto-confrontation vidéo', 'Engagement personnel'],
    },
  ],
  note: "Un conducteur véritablement compétent maîtrise les 4 niveaux. La formation initiale couvre principalement les niveaux 1 et 2, le 3 et le 4 se construisent avec l'expérience.",
};

// =============================================================================
// III. REMC — Référentiel pour une Éducation à une Mobilité Citoyenne
// =============================================================================
export const REMC = {
  intitule: "Le REMC — Référentiel pour une Éducation à une Mobilité Citoyenne",
  dateEntreeVigueur: "2014",
  description: "Référentiel officiel actuel construit à partir de la matrice GDE. Ne donne PAS une liste exhaustive de compétences : il fixe des cadres et des intentions éducatives.",
  enjeuxEducatifs: [
    "L'accidentalité des usagers âgés de 14 à 25 ans",
    "L'accidentalité liée à l'usage des deux-roues motorisés",
    "La sécurité des usagers vulnérables (piétons, cyclistes)",
    "La lutte contre l'usage de substances psycho-actives (alcool, drogues, médicaments)",
    "La poursuite de la baisse des vitesses moyennes",
    "La mobilité durable et citoyenne",
    "Le risque routier professionnel",
    "Le vieillissement de la population",
  ],
  quatreNotions: [
    "L'usager responsable",
    "Le déplacement rationnel",
    "Devenir un conducteur compétent",
    "La gestion de l'environnement, des pressions extérieures",
  ],
  competencesGlobales: [
    {
      numero: 1,
      intitule: "Assumer personnellement ses responsabilités citoyennes, juridiques et sociales",
      lienNiveauGDE: 4,
    },
    {
      numero: 2,
      intitule: "Utiliser un véhicule à moteur rationnellement et en sécurité",
      lienNiveauGDE: 3,
    },
    {
      numero: 3,
      intitule: "Préparer ses trajets et conduire le véhicule de façon autonome dans les situations de circulation simples ou complexes",
      lienNiveauGDE: 2,
    },
    {
      numero: 4,
      intitule: "Prendre en compte les facteurs entraînant une dégradation du système Homme–Véhicule–Environnement, prendre les décisions qui permettent d'y faire face, mettre en œuvre les mesures préventives",
      lienNiveauGDE: 1,
    },
  ],
  note: "Le REMC n'a PAS supprimé la PPO (Pédagogie Par Objectif) — il l'intègre dans une logique plus globale.",
};

// =============================================================================
// IV. PÉDAGOGIE PAR OBJECTIFS (PPO)
// =============================================================================
export const PEDAGOGIE_PAR_OBJECTIFS = {
  intitule: "La Pédagogie par Objectifs (PPO)",
  description: "Approche pédagogique née dans les années 1960 (Bloom, Mager), formalisée pour la formation conducteur en France à partir du PNF (2002). Centrée sur l'élève : on définit ce que l'élève DOIT être capable de faire à la fin de la séance.",
  principes: [
    "L'apprentissage est orienté vers un OBJECTIF mesurable",
    "L'élève est ACTEUR de son apprentissage",
    "L'évaluation porte sur l'ATTEINTE de l'objectif, pas sur la présence",
    "La progression suit une logique du simple au complexe",
  ],
  typesObjectifs: [
    {
      type: 'général',
      intitule: 'Objectif général',
      description: "Vision large d'une séance ou d'un module. Souvent commencé par : « À l'issue de la séance, l'élève sera capable de… »",
      exemple: "À l'issue de la séance, l'élève sera capable de circuler en agglomération en sécurité.",
    },
    {
      type: 'spécifique',
      intitule: 'Objectif spécifique',
      description: "Découpe l'objectif général en sous-éléments. DOIT respecter 3 qualités : être SPÉCIFIQUE, MESURABLE et ATTEIGNABLE.",
      qualites: ["Spécifique (précis, ciblé)", "Mesurable (vérifiable par observation ou test)", "Atteignable (réaliste pour l'élève et le temps imparti)"],
      exemple: "L'élève adaptera sa vitesse à 50 km/h en agglomération sur l'ensemble du parcours.",
    },
    {
      type: 'opérationnel',
      intitule: 'Objectif opérationnel',
      description: "Objectif décliné en COMPORTEMENT OBSERVABLE. Utilise des VERBES D'ACTION précis (lister, citer, démontrer, calculer, appliquer…). Inclut la CONDITION et le CRITÈRE de réussite.",
      verbesAUtiliser: ["Citer", "Lister", "Identifier", "Distinguer", "Calculer", "Appliquer", "Démontrer", "Réaliser", "Effectuer", "Adapter"],
      verbesAEviter: ["Comprendre", "Savoir", "Connaître", "Apprendre"],
      structure: "Verbe d'action + Objet + Condition + Critère",
      exemple: "L'élève cite (verbe) 5 panneaux de signalisation (objet) à partir d'un diaporama (condition) avec 100 % de réussite (critère).",
    },
  ],
};

// =============================================================================
// V. MÉTHODES PÉDAGOGIQUES
// =============================================================================
export const METHODES_PEDAGOGIQUES = {
  intitule: "Les méthodes pédagogiques",
  definition: "Une méthode pédagogique est l'ensemble des techniques utilisées par le formateur pour atteindre un objectif d'apprentissage. On distingue deux grandes familles : EXPOSITIVES (le formateur transmet) et ACTIVES (l'élève agit).",
  familles: [
    {
      code: 'expositives',
      intitule: 'Méthodes expositives ou affirmatives',
      description: "Le formateur est CENTRAL. Il expose un savoir, démontre, structure. L'élève RECEVE.",
      pourcentageRetention: '~10-30 %',
      methodes: [
        {
          code: 'magistrale',
          intitule: 'Méthode magistrale',
          description: "Cours classique : le formateur expose, l'élève écoute et prend des notes.",
          avantages: ["Rapide", "Couvre beaucoup de contenu", "Adapté à un grand groupe", "Apporte une structure claire"],
          inconvenients: [
            "Passivité de l'élève → faible mémorisation (~10 %)",
            "Pas de feedback immédiat sur la compréhension",
            "Risque d'ennui sur les longues durées",
            "Inadapté aux apprentissages psychomoteurs (équilibre, freinage)",
          ],
          solutions: [
            "Limiter à 20 min maximum par séquence",
            "Alterner avec des questions ouvertes (méthode interrogative)",
            "Utiliser des supports visuels (schémas, photos, vidéos)",
            "Donner des exemples concrets liés au vécu",
          ],
          quandUtiliser: "Apport de connaissances théoriques structurées (Code de la route, physique, statistiques).",
        },
        {
          code: 'demonstrative',
          intitule: 'Méthode démonstrative',
          description: "Le formateur MONTRE puis l'élève REFAIT. Méthode reine en formation moto sur le plateau.",
          avantages: [
            "Apprentissage moteur efficace (équilibre, maniabilité)",
            "Le formateur reste modèle de référence",
            "Ancrage par l'imitation (Bandura — apprentissage social)",
            "Feedback immédiat possible",
            "Adapté aux gestes techniques",
          ],
          inconvenients: [
            "Ne développe pas l'autonomie de réflexion",
            "Risque de reproduire sans comprendre",
            "Coûteux en temps pour 1 formateur / plusieurs élèves",
          ],
          structure: "1) Démonstration globale du formateur — 2) Démonstration décomposée — 3) Démonstration commentée — 4) Reproduction par l'élève — 5) Feedback et correction",
          quandUtiliser: "Acquisition d'un geste technique (freinage d'urgence, slalom, démarrage en côte).",
        },
      ],
    },
    {
      code: 'actives',
      intitule: 'Méthodes actives',
      description: "L'ÉLÈVE est CENTRAL. Il construit son savoir par l'action, la recherche, l'expérimentation. Le formateur devient FACILITATEUR.",
      pourcentageRetention: '~70-90 %',
      methodes: [
        {
          code: 'decouverte',
          intitule: 'Méthode de la découverte',
          description: "L'élève est mis en situation et CHERCHE par lui-même la solution. Le formateur guide par des questions, ne donne pas la réponse.",
          avantages: ["Engagement maximal", "Mémorisation durable", "Développe l'autonomie"],
          inconvenients: ["Chronophage", "Risque de fausse piste", "Exige un formateur expérimenté"],
          quandUtiliser: "Apprentissage de la conduite anticipative, gestion d'imprévus.",
        },
        {
          code: 'conduite-commentee',
          intitule: 'La conduite commentée',
          description: "L'élève (ou le formateur) verbalise À VOIX HAUTE tout ce qu'il voit, anticipe et décide pendant qu'il conduit.",
          avantages: [
            "Externalise le processus de décision invisible",
            "Permet au formateur d'identifier les manques",
            "Développe l'attention visuelle et l'anticipation",
            "Très efficace pour la 2RM (lecture de route)",
          ],
          inconvenients: ["Demande effort cognitif important", "Inhibant au début"],
          quandUtiliser: "Tout au long de la formation circulation pour 2RM.",
        },
        {
          code: 'etude-de-cas',
          intitule: "L'étude de cas",
          description: "Le formateur présente une SITUATION RÉELLE (accident, conflit, dilemme) et demande à l'élève d'analyser, de prendre une décision et de la justifier.",
          avantages: [
            "Mobilise des connaissances multiples",
            "Apprend à raisonner sous incertitude",
            "Très adapté aux niveaux 3 et 4 de la matrice GDE",
            "Méthode reine de l'entretien technique CCS",
          ],
          inconvenients: ["Sélection des cas critique", "Demande préparation"],
          quandUtiliser: "Formation continue, sensibilisation, préparation CCS.",
        },
        {
          code: 'simulation',
          intitule: 'La simulation',
          description: "Reproduction artificielle d'une situation réelle (simulateur de conduite, vidéo immersive, lunettes alcool).",
          avantages: [
            "Sécurité totale (pas de risque réel)",
            "Reproduit des situations dangereuses (verglas, choc)",
            "Permet la répétition",
            "Très impactant émotionnellement",
          ],
          inconvenients: ["Coût matériel élevé", "Transfert vers le réel parfois limité"],
          quandUtiliser: "Sensibilisation alcool/drogue, première séance de freinage d'urgence.",
        },
        {
          code: 'jeu-de-role',
          intitule: 'Le jeu de rôle',
          description: "Les élèves incarnent des PERSONNAGES dans une situation (livreur sous pression, ado dans un groupe). Permet de vivre la situation de l'intérieur.",
          avantages: [
            "Forte implication émotionnelle",
            "Travaille les compétences sociales",
            "Très adapté aux animations de sensibilisation",
            "Construit l'empathie",
          ],
          inconvenients: ["Pudeur de certains élèves", "Maîtrise d'animation indispensable"],
          quandUtiliser: "Animation associative, intervention scolaire, prévention.",
        },
      ],
    },
  ],
};

// =============================================================================
// VI. ÉVALUATIONS
// =============================================================================
export const EVALUATIONS = {
  intitule: "Les évaluations en formation conducteur",
  description: "L'évaluation est constitutive de l'apprentissage. Elle se décline en plusieurs moments et types selon son but.",
  typesParMoment: [
    {
      moment: 'En dehors de la formation',
      types: [
        {
          code: 'diagnostique',
          intitule: 'Évaluation diagnostique (initiale)',
          quand: "Avant la formation",
          but: "Identifier le niveau initial de l'élève, ses pré-requis, ses représentations.",
          outils: ["Questionnaire d'inscription", "Premier entretien", "Test sur le terrain (évaluation de départ)"],
          consequence: "Adapter la progression individuelle.",
        },
        {
          code: 'sommative-certificative',
          intitule: 'Évaluation sommative (certificative)',
          quand: "À la fin de la formation",
          but: "Vérifier l'acquisition des compétences requises pour la certification (ETM, ETG, ETC, circulation).",
          outils: ["Examen blanc", "ETG/ETM officiel", "Examen pratique"],
          consequence: "Délivrance ou non du permis.",
        },
      ],
    },
    {
      moment: 'Dans la formation',
      types: [
        {
          code: 'formative',
          intitule: 'Évaluation formative',
          quand: "Pendant la formation, en continu",
          but: "Donner du FEEDBACK à l'élève pour l'aider à progresser. Pas de note finale.",
          outils: [
            "Observation continue",
            "Questions reformulées",
            "QCM de fin de séance",
            "Auto-évaluation par l'élève",
            "Grille d'observation du formateur",
          ],
          consequence: "Ajustement immédiat de l'enseignement.",
        },
      ],
    },
  ],
  questionnement: {
    intitule: "Le questionnement comme outil d'évaluation",
    description: "Le formateur évalue en permanence par les QUESTIONS qu'il pose. Plusieurs types selon le but recherché.",
    types: [
      {
        type: 'fermée',
        description: "Réponse oui/non ou très courte",
        exemple: "« Quelle est la vitesse en agglomération ? »",
        usage: "Vérifier une connaissance précise.",
      },
      {
        type: 'ouverte',
        description: "Demande une réponse développée",
        exemple: "« Pourquoi est-il dangereux de freiner en virage ? »",
        usage: "Évaluer la compréhension et le raisonnement.",
      },
      {
        type: 'à choix multiples',
        description: "Plusieurs propositions, une ou plusieurs bonnes",
        exemple: "QCM ETM",
        usage: "Évaluer rapidement plusieurs notions.",
      },
      {
        type: 'reformulation',
        description: "« Si je comprends bien, tu veux dire que… »",
        exemple: "« Donc selon toi, on ne freine jamais en virage ? »",
        usage: "Vérifier la compréhension mutuelle, faire préciser.",
      },
    ],
  },
};

// =============================================================================
// VII. RELATION PÉDAGOGIQUE
// =============================================================================
export const RELATION_PEDAGOGIQUE = {
  intitule: "La relation pédagogique",
  description: "La relation entre le formateur et l'élève est le LIEN qui rend possible l'apprentissage. Elle se construit sur le SAVOIR partagé.",
  triangle: {
    intitule: "Le triangle pédagogique (Houssaye)",
    description: "Tout acte pédagogique met en relation 3 pôles : le SAVOIR, le FORMATEUR et l'APPRENANT. Le formateur peut privilégier une face plutôt qu'une autre.",
    faces: [
      { axe: 'Enseigner', poles: 'Formateur ↔ Savoir', risque: "L'élève devient spectateur." },
      { axe: 'Former', poles: 'Formateur ↔ Apprenant', risque: "Le savoir devient secondaire (séduction)." },
      { axe: 'Apprendre', poles: 'Apprenant ↔ Savoir', risque: "Le formateur peut être contourné." },
    ],
  },
  roles: {
    formateur: {
      intitule: "Rôle du formateur",
      missions: [
        "Détenteur du savoir (mais pas seul détenteur)",
        "Concepteur de la progression",
        "Animateur de la séance",
        "Évaluateur",
        "Modèle de référence (notamment en moto)",
        "Facilitateur de l'apprentissage",
      ],
      postures: [
        "Bienveillant mais exigeant",
        "Neutre (politique, religieuse, marketing)",
        "Confidentiel",
        "Ponctuel et professionnel",
      ],
    },
    eleve: {
      intitule: "Ce qu'il faut accepter en tant qu'élève",
      attitudes: [
        "Accepter de ne pas savoir (humilité)",
        "Accepter de se tromper (apprentissage par essai-erreur)",
        "Accepter la correction (feedback)",
        "S'impliquer activement (sortir de la passivité)",
        "Être patient avec soi-même",
      ],
    },
    lien: {
      intitule: "Ce qui fait le lien entre l'élève et le formateur",
      element: "LE SAVOIR partagé. C'est l'objectif d'apprentissage qui justifie la relation. Sans savoir à transmettre, la relation perd son sens.",
    },
  },
};

// =============================================================================
// VIII. PYRAMIDE DE MASLOW (motivations)
// =============================================================================
export const PYRAMIDE_MASLOW = {
  intitule: "La pyramide d'Abraham Maslow (1943)",
  description: "Théorie de la HIÉRARCHIE des besoins. Un individu ne peut s'investir dans un besoin supérieur que si les besoins inférieurs sont satisfaits. Très utile pour comprendre les motivations des élèves.",
  niveaux: [
    {
      ordre: 5,
      intitule: 'Accomplissement de soi',
      description: 'Réaliser son potentiel, se dépasser.',
      exempleConduite: "« Je passe le permis A pour me prouver que j'en suis capable, par passion. »",
    },
    {
      ordre: 4,
      intitule: 'Estime de soi',
      description: 'Reconnaissance par soi et par les autres.',
      exempleConduite: "« Avoir une belle moto me donne confiance. Mon entourage m'admire. »",
    },
    {
      ordre: 3,
      intitule: 'Appartenance et amour',
      description: 'Famille, amitiés, groupe.',
      exempleConduite: "« Je passe le permis pour pouvoir sortir avec mes amis motards. »",
    },
    {
      ordre: 2,
      intitule: 'Sécurité',
      description: 'Sécurité physique, financière, professionnelle.',
      exempleConduite: "« Je passe le permis pour aller au travail en sécurité. »",
    },
    {
      ordre: 1,
      intitule: 'Besoins physiologiques',
      description: 'Manger, boire, dormir, respirer.',
      exempleConduite: "Un élève fatigué, affamé ou stressé ne PEUT PAS apprendre. À vérifier en début de séance.",
    },
  ],
  applicationsPedagogiques: [
    "Vérifier les conditions matérielles en début de séance (élève en forme ? équipement OK ?)",
    "Créer un climat de sécurité psychologique (pas de moquerie, droit à l'erreur)",
    "Valoriser les réussites pour nourrir l'estime de soi",
    "Identifier les motivations profondes pour adapter le discours",
  ],
};

// =============================================================================
// IX. DÉFINITIONS COMPLÉMENTAIRES
// =============================================================================
export const DEFINITIONS_COMPLEMENTAIRES = [
  {
    terme: 'Attitudes',
    definition: "Disposition mentale durable d'un individu vis-à-vis d'un objet, d'une personne, d'une situation. Inclut une composante affective (j'aime/je n'aime pas), cognitive (je pense que…) et conative (je suis prêt à…).",
    exemple: "Attitude positive vis-à-vis de la moto : « la moto, c'est la liberté ». Attitude négative : « la moto, c'est dangereux ».",
    usage: "Les attitudes orientent les comportements. Pédagogiquement, on cherche à modifier les attitudes pour modifier les comportements.",
  },
  {
    terme: 'Représentations',
    definition: "Image mentale que se fait un individu d'un objet ou d'un phénomène, construite à partir de son expérience et des informations reçues.",
    exemple: "Représentation du « bon motard » : casqué, expérimenté, prudent VS sportif, rapide, sans peur.",
    usage: "Identifier les représentations initiales des élèves en début de séance pour adapter l'apprentissage (méthode de la découverte).",
  },
  {
    terme: 'Croyances',
    definition: "Conviction profonde, souvent non vérifiée, qu'un individu tient pour vraie.",
    exemple: "« Je suis meilleur conducteur que la moyenne » (effet Dunning-Kruger). « L'ABS me protège de tout » (croyance erronée).",
    usage: "Les croyances erronées sont des FREINS pédagogiques. Les déconstruire par la confrontation à des faits (chiffres ONISR, vidéos, démonstrations).",
  },
  {
    terme: 'Motivations',
    definition: "Forces qui POUSSENT un individu à agir. Peut être intrinsèque (plaisir personnel) ou extrinsèque (récompense, contrainte).",
    exemple: "Motivation intrinsèque : passion moto. Motivation extrinsèque : permis obligatoire pour le travail.",
    usage: "Identifier les motivations de chaque élève pour personnaliser l'approche (cf. cas n°20 du référentiel CCS).",
  },
  {
    terme: 'Comportement',
    definition: "Manière OBSERVABLE d'agir d'un individu en réponse à une situation.",
    exemple: "Comportement à risque : franchir un STOP. Comportement sécuritaire : porter ses gants CE.",
    usage: "C'est sur le comportement OBSERVABLE qu'on évalue (jamais sur les intentions). D'où l'importance des objectifs opérationnels (verbes d'action observables).",
  },
];
